import React, { useState, useEffect, useCallback } from "react"
import { FiTrash2, FiPlus, FiRefreshCw, FiLock } from "react-icons/fi"
import toast from "react-hot-toast"
import axiosInstance from "../../../libs/axios"
import { SelectWithSearch2 } from "../../../utils/select-with-search"
import { useAuth } from "../../../provider/auth.provider"

interface BlockedRecord {
  id: number
  user_id: number
  reason: string | null
  user?: { id: number; first_name: string; last_name: string; username: string; employee_code: string | null }
}

interface LimitRecord {
  id: number
  role_id: number
  max_amount: number
  reason: string | null
  role?: { id: number; name: string }
}

interface UserOption { id: number; value: number; label: string }
interface RoleOption { id: number; value: number; label: string }

const userName = (u?: BlockedRecord["user"]) =>
  u ? `${u.first_name} ${u.last_name} (${u.username})` : "—"

const SupplierPaymentRestrictions: React.FC = () => {
  const { hasPermission } = useAuth()
  const [activeTab, setActiveTab] = useState<"user_restriction" | "role_amount_limit">("user_restriction")
  const [blocked, setBlocked] = useState<BlockedRecord[]>([])
  const [limits, setLimits] = useState<LimitRecord[]>([])
  const [loading, setLoading] = useState(false)

  // Block form
  const [blockUser, setBlockUser] = useState<UserOption | null>(null)
  const [blockReason, setBlockReason] = useState("")
  const [blockSaving, setBlockSaving] = useState(false)

  // Limit form
  const [limitRole, setLimitRole] = useState<RoleOption | null>(null)
  const [limitAmount, setLimitAmount] = useState("")
  const [limitReason, setLimitReason] = useState("")
  const [limitSaving, setLimitSaving] = useState(false)

  const userMapOption = useCallback((u: any) => ({
    id: u.id, value: u.id,
    label: `${u.first_name} ${u.last_name} (${u.username})`,
  }), [])

  const roleMapOption = useCallback((r: any) => ({
    id: r.id, value: r.id, label: r.name,
  }), [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [b, l] = await Promise.all([
        axiosInstance.get("/supplier-payment/restrictions/blocked-users"),
        axiosInstance.get("/supplier-payment/restrictions/limits"),
      ])
      if (b.data.success) setBlocked(b.data.data.records || [])
      if (l.data.success) setLimits(l.data.data.records || [])
    } catch {
      toast.error("Failed to load restriction data")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [])

  const handleBlock = async () => {
    if (!blockUser) { toast.error("Please select a user"); return }
    setBlockSaving(true)
    try {
      await axiosInstance.post("/supplier-payment/restrictions/block-user", {
        user_id: blockUser.id,
        reason: blockReason.trim() || undefined,
      })
      toast.success("User blocked from supplier payment requests")
      setBlockUser(null)
      setBlockReason("")
      fetchData()
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to block user")
    } finally {
      setBlockSaving(false)
    }
  }

  const handleUnblock = async (userId: number) => {
    try {
      await axiosInstance.delete(`/supplier-payment/restrictions/block-user/${userId}`)
      toast.success("User unblocked")
      fetchData()
    } catch {
      toast.error("Failed to unblock user")
    }
  }

  const handleSetLimit = async () => {
    if (!limitRole) { toast.error("Please select a role"); return }
    if (!limitAmount || Number(limitAmount) <= 0) { toast.error("Enter a valid amount"); return }
    setLimitSaving(true)
    try {
      await axiosInstance.post("/supplier-payment/restrictions/set-limit", {
        role_id: limitRole.id,
        max_amount: Number(limitAmount),
        reason: limitReason.trim() || undefined,
      })
      toast.success("Limit saved")
      setLimitRole(null)
      setLimitAmount("")
      setLimitReason("")
      fetchData()
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to set limit")
    } finally {
      setLimitSaving(false)
    }
  }

  const handleRemoveLimit = async (roleId: number) => {
    try {
      await axiosInstance.delete(`/supplier-payment/restrictions/limit/${roleId}`)
      toast.success("Limit removed")
      fetchData()
    } catch {
      toast.error("Failed to remove limit")
    }
  }

  if (!hasPermission("supplier_payment", "manage_restrictions")) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <FiLock className="w-12 h-12 text-gray-300 mb-4" />
        <h2 className="text-lg font-semibold text-gray-700">Access Denied</h2>
        <p className="text-sm text-gray-500 mt-1">You don't have permission to manage supplier payment restrictions.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Supplier Payment Restrictions</h1>
          <p className="text-sm text-gray-500 mt-1">Block specific users or set per-role amount limits for supplier payment requests</p>
        </div>
        <button onClick={fetchData} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">
          <FiRefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex">
          {(["user_restriction", "role_amount_limit"] as const).map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? "border-blue-800 text-blue-800"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab === "user_restriction" ? "User Restriction" : "Role Amount Limit"}
            </button>
          ))}
        </nav>
      </div>

      {/* User Restriction Tab */}
      {activeTab === "user_restriction" && (
        <div className="bg-white border border-gray-200 rounded shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-800">Blocked Users</h2>
            <p className="text-xs text-gray-500 mt-1">Users in this list cannot raise supplier payment requests</p>
          </div>
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">User</label>
                <SelectWithSearch2
                  placeholder="Search or select user (min 3 chars)"
                  value={blockUser}
                  onChange={(opt) => setBlockUser(opt ? { id: Number(opt.id ?? opt.value), value: Number(opt.value), label: opt.label } : null)}
                  apiEndpoint="/users/list"
                  mapOption={userMapOption}
                  className="w-full"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Reason (optional)</label>
                <input
                  type="text"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder="Reason for blocking"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={handleBlock}
                disabled={blockSaving || !blockUser}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiPlus className="w-4 h-4" />
                {blockSaving ? "Saving..." : "Block User"}
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {blocked.length === 0 ? (
                  <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400">No blocked users</td></tr>
                ) : blocked.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{userName(r.user)}</td>
                    <td className="px-4 py-3 text-gray-500">{r.reason || "—"}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleUnblock(r.user_id)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50">
                        <FiTrash2 className="w-3 h-3" /> Unblock
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Role Amount Limit Tab */}
      {activeTab === "role_amount_limit" && (
        <div className="bg-white border border-gray-200 rounded shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-800">Per-Role Amount Limits</h2>
            <p className="text-xs text-gray-500 mt-1">Set the maximum supplier payment amount users of a given role can request</p>
          </div>
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
                <SelectWithSearch2
                  placeholder="Search or select role (min 3 chars)"
                  value={limitRole}
                  onChange={(opt) => setLimitRole(opt ? { id: Number(opt.id ?? opt.value), value: Number(opt.value), label: opt.label } : null)}
                  apiEndpoint="/roles/list"
                  dataKey="roles"
                  mapOption={roleMapOption}
                  className="w-full"
                />
              </div>
              <div className="w-44">
                <label className="block text-xs font-medium text-gray-700 mb-1">Max Amount (₹)</label>
                <input
                  type="number"
                  value={limitAmount}
                  onChange={(e) => setLimitAmount(e.target.value)}
                  placeholder="e.g. 50000"
                  min={1}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Reason (optional)</label>
                <input
                  type="text"
                  value={limitReason}
                  onChange={(e) => setLimitReason(e.target.value)}
                  placeholder="Reason for limit"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={handleSetLimit}
                disabled={limitSaving || !limitRole || !limitAmount}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-700 text-white rounded hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FiPlus className="w-4 h-4" />
                {limitSaving ? "Saving..." : "Set Limit"}
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Max Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {limits.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No limits set</td></tr>
                ) : limits.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{r.role?.name || `Role #${r.role_id}`}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">₹{Number(r.max_amount).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-gray-500">{r.reason || "—"}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleRemoveLimit(r.role_id)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50">
                        <FiTrash2 className="w-3 h-3" /> Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default SupplierPaymentRestrictions
